/**
 * Awaited warehouse-sources populate for the Sources subtab (ADR-047 addendum).
 *
 * Calls `POST /api/documents/populate-sources`, AWAITED, once per patient
 * view, and again on an explicit "Refresh sources" request. This deliberately
 * replaced an earlier fire-and-forget backend design (a `BackgroundTasks`
 * populate scheduled from `GET /api/documents`): that failed SILENTLY on a
 * Cloud Run revision with `cpu-throttling=true` (CPU throttled to near-zero
 * outside request handling, so the populate could stall indefinitely with
 * nobody told), and this hook's predecessor had nothing to notice even a
 * successful completion — no refetch, no poll, no invalidation. "Do not block
 * the page render" means the profile renders immediately; it does not mean
 * the populate should run detached from any caller. `populating` is the
 * caller's signal to show a visible loading state for the duration;
 * `result.populated` is the signal to refetch the document list.
 *
 * Cloud Tasks async path: when `CLOUD_TASKS_ABSORPTION_QUEUE` is configured
 * on the backend, `POST /api/documents/populate-sources` returns immediately
 * with `status: "in_progress"` after enqueuing a worker task. This hook
 * detects that and polls `GET /api/documents/absorption-status` every
 * {@link POLL_INTERVAL_MS} ms until the status resolves to `succeeded` or
 * `failed`. `populating` stays `true` throughout so the spinner is
 * uninterrupted.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";

const POPULATE_ENDPOINT = "/api/documents/populate-sources";
const STATUS_ENDPOINT = "/api/documents/absorption-status";
/** Polling interval while waiting for an async Cloud Tasks worker to finish. */
const POLL_INTERVAL_MS = 2000;
/** Maximum wall-clock time (ms) to keep polling before giving up. */
const POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/** The status the backend reports for one populate attempt. */
export type PopulateStatus =
  | "succeeded"
  | "failed"
  | "in_progress"
  | "skipped_not_due"
  | "not_applicable";

/** Per-status counts from a successful populate (materialised/skipped/failed sources). */
export interface PopulateCounts {
  materialised: number;
  skipped: number;
  failed: number;
}

/** The parsed response of a populate-sources call. */
export interface PopulateResult {
  populated: boolean;
  status: PopulateStatus;
  last_error: string | null;
  counts: PopulateCounts | null;
}

/** The parsed response of a GET /api/documents/absorption-status call. */
interface AbsorptionStatusResponse {
  patient_id: string;
  status?: string;
  last_error?: string | null;
  populated?: boolean;
}

/** The result surface returned by {@link usePopulateSources}. */
export interface UsePopulateSourcesResult {
  /** True while the awaited populate call is in flight — show a loading state. */
  populating: boolean;
  /** The most recent completed populate result, or null before any call resolves. */
  result: PopulateResult | null;
  /** Explicitly (re-)run the populate, e.g. from a "Refresh sources" button. */
  refresh: () => void;
}

/**
 * Populate a patient's warehouse sources on view, awaited, with loading state.
 *
 * Mirrors {@link useDocumentLibrary}'s reload-token pattern: the fetch runs
 * inline inside the effect (not via an externally-memoized callback) so a
 * patient switch or an explicit refresh re-triggers exactly one run, and the
 * effect's own cleanup guards against a stale response landing after the
 * patient has already changed again.
 *
 * @param patientId - The active patient id.
 * @returns The loading flag, the last result, and an explicit refresh action.
 */
export function usePopulateSources(
  patientId: string,
): UsePopulateSourcesResult {
  const [populating, setPopulating] = useState(false);
  const [result, setResult] = useState<PopulateResult | null>(null);
  const [token, setToken] = useState(0);
  const forceRef = useRef(false);

  useEffect(() => {
    let active = true;
    const isRefresh = forceRef.current;
    forceRef.current = false;

    /**
     * Poll GET /api/documents/absorption-status until the backend reports a
     * terminal state (succeeded / failed) or we hit the timeout.
     * Returns a resolved PopulateResult.
     */
    async function pollUntilDone(): Promise<PopulateResult> {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      while (active && Date.now() < deadline) {
        await new Promise<void>((res) => setTimeout(res, POLL_INTERVAL_MS));
        if (!active) break;
        const statusResp = await apiUtils.fetchData<AbsorptionStatusResponse>(
          `${STATUS_ENDPOINT}?patient_id=${encodeURIComponent(patientId)}`,
          "GET",
        );
        if (!active) break;
        if (isApiError(statusResp)) {
          logger.devOnly.warn(
            "usePopulateSources: absorption-status poll failed",
          );
          continue;
        }
        const s = statusResp.status ?? "";
        if (s === "succeeded") {
          return {
            populated: true,
            status: "succeeded",
            last_error: null,
            counts: null,
          };
        }
        if (s === "failed") {
          return {
            populated: false,
            status: "failed",
            last_error: statusResp.last_error ?? null,
            counts: null,
          };
        }
        // still in_progress — keep polling
      }
      // timed out or unmounted
      return {
        populated: false,
        status: "in_progress",
        last_error: "Polling timed out",
        counts: null,
      };
    }

    async function run(): Promise<void> {
      setPopulating(true);
      setResult(null);
      try {
        const resp = await apiUtils.fetchData<PopulateResult>(
          POPULATE_ENDPOINT,
          "POST",
          { patient_id: patientId, refresh: isRefresh },
        );
        if (!active) return;
        if (isApiError(resp)) {
          logger.devOnly.warn("usePopulateSources: populate failed");
          setResult({
            populated: false,
            status: "failed",
            last_error: resp.error,
            counts: null,
          });
          return;
        }
        // Cloud Tasks async path: the backend enqueued a task and returned
        // in_progress immediately. Poll until the worker finishes.
        if (resp.status === "in_progress") {
          const polled = await pollUntilDone();
          if (!active) return;
          setResult(polled);
          return;
        }
        setResult(resp);
      } finally {
        if (active) setPopulating(false);
      }
    }

    run().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("usePopulateSources: populate threw", {
        error: String(err),
      });
      setResult({
        populated: false,
        status: "failed",
        last_error: err instanceof Error ? err.message : String(err),
        counts: null,
      });
      setPopulating(false);
    });

    return () => {
      active = false;
    };
  }, [patientId, token]);

  const refresh = useCallback((): void => {
    forceRef.current = true;
    setToken((t) => t + 1);
  }, []);

  return { populating, result, refresh };
}
